import { DEFAULT_PM_RULE_SOURCE } from '@atlas/types'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import type { LookupListRow } from '@/services/workfront-mappings.service'
import { ListState, type ListStateProps } from './ListState'
import { RowStatusBadge } from './MappingBadges'

export interface PmRulesTableProps extends Pick<ListStateProps, 'isLoading' | 'isError' | 'onRetry'> {
	rows: LookupListRow[]
	/** A user's display name by id. */
	nameOf: (userId: string) => string
	/** Whether a user is an active PM; a rule to anyone else is skipped until it is fixed. */
	isActivePm: (userId: string) => boolean
	onEdit: (row: LookupListRow) => void
}

const geographyOf = (row: LookupListRow) => row.sourceLabel ?? row.row.sourceValue

/** The rules by geography path, the default last. */
export const sortedRules = (rows: LookupListRow[]): LookupListRow[] =>
	[...rows].sort((a, b) => {
		const defaultFirst =
			Number(a.row.sourceValue === DEFAULT_PM_RULE_SOURCE) - Number(b.row.sourceValue === DEFAULT_PM_RULE_SOURCE)
		return defaultFirst || geographyOf(a).localeCompare(geographyOf(b))
	})

/** The Region → PM rules: each geography with its path, the PM it goes to, and its draft status. */
export function PmRulesTable({ rows, nameOf, isActivePm, onEdit, ...state }: PmRulesTableProps) {
	return (
		<ListState
			noun="PM rules"
			{...state}
			isEmpty={rows.length === 0}
			emptyTitle="No PM rules yet: work orders from Workfront arrive with no PM"
		>
			<Table aria-label="PM rules">
				<TableHeader>
					<TableRow>
						<TableHead>Geography</TableHead>
						<TableHead>PM</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>
							<span className="sr-only">Actions</span>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedRules(rows).map((rule) => (
						<TableRow key={rule.key}>
							<TableCell className="font-medium">{geographyOf(rule)}</TableCell>
							<TableCell>
								<span className="inline-flex items-center gap-2">
									{nameOf(rule.row.targetValue)}
									{rule.status !== 'deleted' && !isActivePm(rule.row.targetValue) ? (
										<Badge size="sm" variant="danger">
											not an active PM
										</Badge>
									) : null}
								</span>
							</TableCell>
							<TableCell>
								<RowStatusBadge status={rule.status} />
							</TableCell>
							<TableCell>
								<Button
									size="sm"
									variant="ghost"
									aria-label={`Edit ${geographyOf(rule)}`}
									isDisabled={rule.status === 'deleted'}
									onPress={() => onEdit(rule)}
								>
									Edit
								</Button>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}
